import React, { useState, useEffect, useRef, useMemo } from "react";
import court from "../../assets/images/court.png";
import "./courtInputs.css";

function CourtInputs({ numCourts, bookingDate, uniformTiming, slots, totalCourts }) {

    // Utility to get the next available slot (for min-hour cutoff)
    const getCurrentSlot = () => {
        const now = new Date();
        const hour = now.getHours();
        const min = now.getMinutes();

        // Calculate which 30-minute slot we're currently in
        let currentSlot = hour * 2 + (min >= 30 ? 1 : 0);

        // Return the NEXT available slot (the one that hasn't started yet)
        let nextSlot = currentSlot + 1;

        if (nextSlot > 47) nextSlot = 47;
        return nextSlot;
    };

    // -------- Matrix Build: [court][slot] is true if free -----------
    const courtSlotMatrix = useMemo(() => {
        // Each slot: {freeCourts: [courtNums]}
        const mat = Array.from({ length: totalCourts }, () => Array(48).fill(true));
        slots?.forEach((s) => {
            for (let i = 1; i <= totalCourts; i++) {
                mat[i - 1][s.slot] = s.freeCourts.includes(i);
            }
        });
        return mat;
    }, [slots]);

    // ---- Helpers ----
    const minSlot = useMemo(() => {
        // Compute if bookingDate is today
        const now = new Date();
        const today = now.toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
        const chosen = bookingDate.toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
        if (today === chosen) return getCurrentSlot();
        return 0;
    }, [bookingDate]);

    // ----------- Per-court input state (start, duration, court assignment) -----------
    const [courtInputsArr, setCourtInputsArr] = useState(
        Array.from({ length: numCourts }, (_, i) => ({
            court: "",
            startTime: "",
            duration: "",
        }))
    );

    // Reset state on mode/numCourts change
    useEffect(() => {
        setCourtInputsArr((prev) => {
            const arr = Array.from({ length: numCourts }, (_, i) =>
                prev[i] ? { ...prev[i] } : { court: "", startTime: "", duration: "" }
            );
            // truncate
            return arr.slice(0, numCourts);
        });
    }, [numCourts, uniformTiming]);

    // Always clear on uniform/custom toggle
    const prevUniform = useRef(uniformTiming);
    useEffect(() => {
        if (prevUniform.current !== uniformTiming) {
            setCourtInputsArr(Array.from({ length: numCourts }, () => ({
                court: "",
                startTime: "",
                duration: "",
            })));
            prevUniform.current = uniformTiming;
        }
    }, [uniformTiming, numCourts]);

    // ----------- Utility: get all possible [start, duration, assigned court(s)] -----------

    // For UNIFORM: Find all (start, duration) pairs where at least `numCourts` courts are free
    /* ---------- UNIFORM OPTIONS (fixed) ---------- */
    const uniformOptions = useMemo(() => {
        const out = [];
        for (let slot = minSlot; slot <= 47; slot++) {
            // build [{ court, maxRun }] for every court that’s free at `slot`
            const perCourt = [];
            for (let c = 0; c < totalCourts; c++) {
                let run = 0;
                while (
                    slot + run < 48 &&
                    courtSlotMatrix[c][slot + run] &&
                    run < 8 // cap 4 h
                ) {
                    run++;
                }
                if (run > 0) perCourt.push({ court: c + 1, maxRun: run });
            }

            // Need at least numCourts courts
            if (perCourt.length < numCourts) continue;

            // 🔑  Sort by run‐length desc, then court#, so we grab the “longest-lasting” courts
            perCourt.sort((a, b) =>
                b.maxRun !== a.maxRun ? b.maxRun - a.maxRun : a.court - b.court
            );

            const bestCourts = perCourt.slice(0, numCourts);
            const maxUniformRun = Math.min(
                ...bestCourts.map((c) => c.maxRun)
            ); // shortest run among chosen courts

            const maxDurHrs = Math.min(
                maxUniformRun / 2,
                4,
                (48 - slot) / 2
            );

            if (maxDurHrs > 0) {
                // Fix the hour/minute calculation - each slot is 30 minutes from midnight
                const totalMinutes = slot * 30;
                const hour = Math.floor(totalMinutes / 60);
                const minute = totalMinutes % 60;

                out.push({
                    slotIdx: slot,
                    value: slot / 2,
                    hour: hour,
                    minute: minute,
                    maxDurHrs,
                    courts: bestCourts.map((c) => c.court),
                });
            }
        }
        return out;
    }, [courtSlotMatrix, numCourts, minSlot]);

    // For CUSTOM: For each court input, given previous courts, find all valid [start, duration, court]
    const customOptionsArr = useMemo(() => {
        // Build a scratch matrix for each court selection, so later courts can't overlap earlier
        const usedMatrix = Array.from({ length: totalCourts }, () => Array(48).fill(false));
        const optionsArr = [];
        for (let idx = 0; idx < numCourts; idx++) {
            let options = [];
            // for each slot, for each court, if not used, see max run
            for (let slot = minSlot; slot <= 47; slot++) {
                let found = false;
                let foundCourt = null;
                let foundMaxRun = 0;
                for (let c = 0; c < totalCourts; c++) {
                    // court must be available for at least 1
                    let run = 0;
                    while (
                        slot + run < 48 &&
                        courtSlotMatrix[c][slot + run] &&
                        !usedMatrix[c][slot + run] &&
                        run < 8
                    ) {
                        run++;
                    }
                    if (
                        run > 0 &&
                        (!found || run > foundMaxRun)
                    ) {
                        found = true;
                        foundCourt = c + 1;
                        foundMaxRun = run;
                    }
                }
                if (found) {
                    // Fix the hour/minute calculation - each slot is 30 minutes from midnight
                    const totalMinutes = slot * 30;
                    const hour = Math.floor(totalMinutes / 60);
                    const minute = totalMinutes % 60;

                    options.push({
                        slotIdx: slot,
                        value: slot / 2,
                        hour: hour,
                        minute: minute,
                        court: foundCourt,
                        maxDurHrs: Math.min(foundMaxRun / 2, 4, (48 - slot) / 2),
                    });
                }
            }
            // Once a court is chosen by the user, mark it used
            const selectedStart = courtInputsArr[idx]?.startTime;
            const selectedDur = courtInputsArr[idx]?.duration;
            const selectedSlot = selectedStart ? Math.round(parseFloat(selectedStart) * 2) : null;
            const selectedHrs = selectedDur ? Math.round(parseFloat(selectedDur) * 2) : 0;
            let actualCourtNum = null;
            if (selectedSlot !== null && selectedHrs > 0) {
                // Must use the assigned court from options (lowest number available)
                const opt = options.find((o) => o.value.toString() === selectedStart);
                if (opt) {
                    actualCourtNum = opt.court;
                    for (let s = 0; s < selectedHrs; s++) {
                        usedMatrix[actualCourtNum - 1][selectedSlot + s] = true;
                    }
                }
            }
            optionsArr.push(options);
        }
        return optionsArr;
        // eslint-disable-next-line
    }, [courtSlotMatrix, numCourts, courtInputsArr, minSlot]);

    // ---------- Sanitize court inputs on grid change ----------
    useEffect(() => {
        setCourtInputsArr((prev) => {
            let changed = false;
            const next = prev.map((c, idx) => {
                let { startTime, duration } = c;
                if (uniformTiming === 0) {
                    // Uniform: valid if in uniformOptions
                    const allowed = new Set(uniformOptions.map((o) => o.value.toString()));
                    if (!allowed.has(startTime)) {
                        startTime = "";
                        duration = "";
                    } else {
                        const maxHrs =
                            uniformOptions.find((o) => o.value.toString() === startTime)?.maxDurHrs || 0;
                        if (duration && parseFloat(duration) > maxHrs) duration = "";
                    }
                } else {
                    // Custom: valid if in options for this court
                    const allowed = new Set(
                        (customOptionsArr[idx] || []).map((o) => o.value.toString())
                    );
                    if (!allowed.has(startTime)) {
                        startTime = "";
                        duration = "";
                    } else {
                        const maxHrs =
                            (customOptionsArr[idx] || []).find(
                                (o) => o.value.toString() === startTime
                            )?.maxDurHrs || 0;
                        if (duration && parseFloat(duration) > maxHrs) duration = "";
                    }
                }
                if (startTime !== c.startTime || duration !== c.duration) {
                    changed = true;
                    return { ...c, startTime, duration };
                }
                return c;
            });
            return changed ? next : prev;
        });
        // eslint-disable-next-line
    }, [uniformOptions, customOptionsArr, numCourts, uniformTiming]);

    // ---------- Handlers ----------
    const updateUniformStartTimes = (e) => {
        const startTime = e.target.value;
        setCourtInputsArr((p) => p.map((c) => ({ ...c, startTime, duration: "" })));
    };
    const updateUniformDurations = (e) => {
        const duration = e.target.value;
        setCourtInputsArr((p) => p.map((c) => ({ ...c, duration })));
    };
    const updateCustomStartTimes = (e, idx) => {
        const startTime = e.target.value;
        setCourtInputsArr((prev) =>
            prev.map((c, i) =>
                i === idx ? { ...c, startTime, duration: "" } : c
            )
        );
    };
    const updateCustomDurations = (e, idx) => {
        const duration = e.target.value;
        setCourtInputsArr((prev) =>
            prev.map((c, i) => (i === idx ? { ...c, duration } : c))
        );
    };

    // ------------- RENDER: Uniform -------------
    /* ---------- RENDER uniform dropdowns (unchanged except variable names) ---------- */
    const displayUniformTimeInputs = () => {
        if (uniformTiming !== 0) return null;

        const selectedStart = courtInputsArr[0]?.startTime;
        const selectedOpt = uniformOptions.find(
            (o) => o.value.toString() === selectedStart
        );
        const durOpts = selectedOpt
            ? Array.from(
                { length: Math.floor(selectedOpt.maxDurHrs / 0.5) },
                (_, i) => (i + 1) * 0.5
            )
            : [];

        return (
            <div className="bookings-console-body-uniform-inputs-cont">
                {/* Start-time dropdown */}
                <select
                    className="bookings-console-body-form-input"
                    name="startTime"
                    required
                    value={selectedStart || ""}
                    onChange={updateUniformStartTimes}
                >
                    <option value="">
                        {uniformOptions.length ? "Select Start Time" : "No available slots"}
                    </option>
                    {uniformOptions.map(({ hour, minute, value }) => {
                        const hour12 = hour % 12 === 0 ? 12 : hour % 12;
                        const minuteStr = minute === 0 ? "00" : "30";
                        const ampm = hour < 12 ? "AM" : "PM";
                        return (
                            <option key={value} value={value}>
                                {`${hour12}:${minuteStr} ${ampm}`}
                            </option>
                        );
                    })}
                </select>

                {/* Duration dropdown */}
                <select
                    className="bookings-console-body-form-input"
                    name="duration"
                    required
                    value={courtInputsArr[0]?.duration || ""}
                    disabled={!selectedStart}
                    onChange={updateUniformDurations}
                >
                    <option value="">Duration</option>
                    {durOpts.map((h) => (
                        <option key={h} value={h}>
                            {h === 0.5 ? "30 Minutes" : `${h} Hour${h > 1 ? "s" : ""}`}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    // ------------- RENDER: Custom -------------
    const displayCustomTimeInputs = (index) => {
        if (uniformTiming !== 1) return null;
        if (!courtInputsArr[index]) return null;

        const { startTime, duration } = courtInputsArr[index];
        const options = customOptionsArr[index] || [];
        const selectedOpt = options.find((o) => o.value.toString() === startTime);

        return (
            <>
                <select
                    className="bookings-console-body-form-court-input"
                    name={`startTime[${index}]`}
                    required
                    value={startTime}
                    onChange={(e) => updateCustomStartTimes(e, index)}
                >
                    <option value="">
                        {options.length ? "Start Time" : "No available slots"}
                    </option>
                    {options.map(({ hour, minute, value }) => {
                        const hour12 = hour % 12 === 0 ? 12 : hour % 12;
                        const minuteStr = minute === 0 ? "00" : "30";
                        const ampm = hour < 12 ? "AM" : "PM";
                        return (
                            <option key={value} value={value}>
                                {`${hour12}:${minuteStr} ${ampm}`}
                            </option>
                        );
                    })}
                </select>

                <select
                    className="bookings-console-body-form-court-input"
                    name={`duration[${index}]`}
                    required
                    value={duration}
                    disabled={!startTime}
                    onChange={(e) => updateCustomDurations(e, index)}
                >
                    <option value="">Duration</option>
                    {selectedOpt &&
                        Array.from(
                            { length: Math.floor(selectedOpt.maxDurHrs / 0.5) },
                            (_, i) => (i + 1) * 0.5
                        ).map((h) => (
                            <option key={h} value={h}>
                                {h === 0.5
                                    ? "30 Minutes"
                                    : `${h} Hour${h > 1 ? "s" : ""}`}
                            </option>
                        ))}
                </select>
                {/* <div style={{ fontSize: 10, color: "#999" }}>
                    Debug: assigned court: {selectedOpt ? selectedOpt.court : ""}
                </div> */}
            </>
        );
    };

    // ----------- Final JSX ---------
    return (
        <>
            {displayUniformTimeInputs()}
            <div className="bookings-console-body-form-courts">
                {Array.from({ length: numCourts }).map((_, idx) => (
                    <div className="bookings-console-body-form-court" key={idx}>
                        <p className="bookings-console-body-form-court-title">
                            Court {["A", "B", "C", "D"][idx] || idx + 1}
                        </p>
                        <div className="bookings-console-body-form-court-body">
                            <img
                                className="bookings-console-body-form-court-body-img"
                                src={court}
                                alt=""
                            />
                        </div>
                        <div className="bookings-console-body-form-court-inputs">
                            {displayCustomTimeInputs(idx)}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

export default CourtInputs;